import {
  Box,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { EllipsisText, formatDecimal, formatInteger } from "@shared";

import { DutyScheduleDistributionPeriodResponse } from "@/api";
import { formatPeriod } from "@/pages/duty-schedule/pages/duty-schedule-detail/components/key-figures/DutyScheduleKeyFigures.utils";
import { useDutyScheduleTableStyles } from "@/pages/duty-schedule/pages/duty-schedule-detail/components/table-layout/DutyScheduleTableLayout.styles";
import { useTranslation } from "@/translations";

interface Props {
  periods: DutyScheduleDistributionPeriodResponse[];
  currentPlanName: string;
}

/**
 * The current Dienstplan (always first, highlighted, regardless of its own status) next to the up to
 * three most recently freigegebene Dienstpläne before it — never affected by the Mitarbeiter:in filter.
 */
export function DutyScheduleDistributionPeriodComparison({ periods, currentPlanName }: Props) {
  const { t, i18n } = useTranslation();
  const classes = useDutyScheduleTableStyles();

  function periodLabel(period: DutyScheduleDistributionPeriodResponse): React.ReactNode {
    if (period.isCurrent) {
      return (
        <Stack minWidth={0}>
          <EllipsisText
            value={t("dutySchedule.detail.distribution.periodComparison.currentPlan")}
            variant="body2"
            fontWeight={700}
          />
          <EllipsisText value={currentPlanName} variant="caption" />
        </Stack>
      );
    }

    return (
      <Stack minWidth={0}>
        <EllipsisText value={period.name} variant="body2" fontWeight={700} />
        <EllipsisText value={formatPeriod(period.periodStart, period.periodEnd)} variant="caption" />
      </Stack>
    );
  }

  function count(value: number): string {
    return formatInteger(Math.round(value), i18n.language);
  }

  // totalWeeklyHours sums Counselor.WeeklyHours, a decimal — part-time counselors commonly have
  // fractional values like 37.5, so it must not be rounded to a whole number like the duty counts.
  function hours(value: number): string {
    return formatDecimal(value, i18n.language);
  }

  // The current Dienstplan's row gets the same tint + weight as a Summe row on the other Dienstplan
  // tables, so "the plan you are looking at" reads as the table's summary line everywhere.
  function rowCellSx(period: DutyScheduleDistributionPeriodResponse) {
    return classes.cellHighlight(period.isCurrent ? "totals" : "none");
  }

  return (
    <Paper variant="outlined">
      <Box px={2} py={1.5}>
        <Typography variant="subtitle1" fontWeight={700}>
          {t("dutySchedule.detail.distribution.periodComparison.title")}
        </Typography>
      </Box>
      <Divider />
      <TableContainer>
        <Table
          size="small"
          aria-label={t("dutySchedule.detail.distribution.periodComparison.tableAria")}
          sx={{ tableLayout: "fixed" }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...classes.headerCell, width: "200px" }}>
                {t("dutySchedule.detail.distribution.periodComparison.periodColumn")}
              </TableCell>
              <TableCell sx={classes.headerCell} width={"75px"} align="right">
                {t("dutySchedule.detail.distribution.periodComparison.hoursColumn")}
              </TableCell>
              <TableCell sx={classes.headerCell} width={"85px"} align="right">
                {t("dutySchedule.detail.distribution.table.aw12")}
              </TableCell>
              <TableCell sx={classes.headerCell} width={"75px"} align="right">
                {t("dutySchedule.detail.distribution.table.aw3")}
              </TableCell>
              <TableCell sx={classes.headerCell} width={"75px"} align="right">
                {t("dutySchedule.detail.distribution.table.bgs")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {periods.map((period) => (
              <TableRow key={`${period.name}-${period.periodStart.toISOString()}`}>
                <TableCell sx={rowCellSx(period)}>{periodLabel(period)}</TableCell>
                <TableCell align="right" sx={rowCellSx(period)}>
                  {hours(period.totalWeeklyHours)}
                </TableCell>
                <TableCell align="right" sx={rowCellSx(period)}>
                  {count(period.aw12Total)}
                </TableCell>
                <TableCell align="right" sx={rowCellSx(period)}>
                  {count(period.aw3Total)}
                </TableCell>
                <TableCell align="right" sx={rowCellSx(period)}>
                  {count(period.bgsTotal)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
